import AddIcon from '@mui/icons-material/Add';
import Alert from '@mui/material/Alert';
import Box from '@mui/material/Box';
import Button from '@mui/material/Button';
import Chip from '@mui/material/Chip';
import Stack from '@mui/material/Stack';
import Typography from '@mui/material/Typography';
import { DataGrid, type GridColDef, type GridRowParams } from '@mui/x-data-grid';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useState } from 'react';
import { errorMessage } from '@/api/common';
import { mediaNetworkApi } from '@/api/media-network/mediaNetworkApi';
import { mediaNetworkKeys } from '@/api/media-network/mediaNetworkQueries';
import type { Advertiser } from '@/models/media-network/Advertiser';
import { useLocalizedStrings } from '../../localization/LocalizedStringsProvider';
import { Perm } from '../../permissions/permissions';
import { usePermissions } from '../../permissions/usePermissions';
import { fillHeight } from '../../theme';
import DataGridDeleteButton from '../common/data-grid/DataGridDeleteButton';
import DataGridEditButton from '../common/data-grid/DataGridEditButton';
import { useToast } from '../common/toast/ToastProvider';
import AdvertiserDialog from './dialogs/AdvertiserDialog';
import AdvertiserGrantsDialog from './dialogs/AdvertiserGrantsDialog';

// The TouchTab advertiser roster: the companies TouchTab itself holds agreements with.
//
// Super-admin only, and that is a real boundary rather than a tidy-menu decision. This is the one list that
// spans every client, so it reveals who else is on the network — competitive information a brand must never
// see. The API returns 403 rather than an empty list; this screen is the second lock, not the only one.
//
// A BRAND-LOCAL advertiser never appears here. Those belong to one brand, are invisible to the rest, and are
// deliberately outside cross-client reporting.
const Advertisers = () => {
    const qc = useQueryClient();
    const toast = useToast();
    const strings = useLocalizedStrings();
    const perms = usePermissions();
    const canCreate = perms.canCreate(Perm.adminConfiguration);
    const canUpdate = perms.canUpdate(Perm.adminConfiguration);
    const canDelete = perms.canDelete(Perm.adminConfiguration);

    const [editing, setEditing] = useState<Advertiser | null>(null);
    const [dialogOpen, setDialogOpen] = useState(false);
    const [granting, setGranting] = useState<Advertiser | null>(null);

    const { data: advertisers, isPending } = useQuery({
        queryKey: mediaNetworkKeys.platformAdvertisers(),
        queryFn: () => mediaNetworkApi.listPlatformAdvertisers(),
    });

    const { mutateAsync: remove } = useMutation({
        mutationFn: (id: string) => mediaNetworkApi.deletePlatformAdvertiser(id),
        onSuccess: async () => {
            toast({ severity: 'success', message: strings.advertiserDeleted });
            await qc.invalidateQueries({ queryKey: mediaNetworkKeys.platformAdvertisers() });
        },
        // Refused while any creative anywhere attributes to them, and the message says so. Surface it rather
        // than a generic failure — it names the action to take instead.
        onError: (e: unknown) => toast({ severity: 'error', message: errorMessage(e, strings.advertiserDeleteError) }),
    });

    const columns: GridColDef<Advertiser>[] = [
        { field: 'name', headerName: strings.name, flex: 2 },
        { field: 'externalId', headerName: strings.advertiserAccountRef, flex: 1 },
        {
            field: 'enabled', headerName: strings.enabled, width: 120,
            renderCell: (params) => (
                <Stack direction="row" sx={{ alignItems: 'center', height: '100%' }}>
                    <Chip
                        size="small"
                        variant="outlined"
                        color={params.row.enabled ? 'success' : 'default'}
                        label={params.row.enabled ? strings.enabled : strings.disabled}
                    />
                </Stack>
            ),
        },
        {
            // Across every client, which is the whole reason this tier exists: one Pepsi, one number.
            field: 'mediaCount', headerName: strings.advertiserCreativesNetworkWide, width: 160,
            renderCell: (params) => (
                <Stack direction="row" sx={{ alignItems: 'center', height: '100%' }}>
                    <Typography variant="body2">{params.row.mediaCount}</Typography>
                </Stack>
            ),
        },
        {
            field: 'actions', headerName: strings.actions, type: 'actions', width: 160,
            getActions: (params: GridRowParams<Advertiser>) => [
                ...(canUpdate ? [
                <Button size="input" variant="outlined" onClick={() => setGranting(params.row)}>
                    {strings.advertiserBrands}
                </Button>,
                ] : []),
                <DataGridEditButton
                    id={params.id.toString()}
                    onClick={() => { setEditing(params.row); setDialogOpen(true); }}
                    aria-label="edit advertiser"
                    readOnly={!canUpdate}
                />,
                <DataGridDeleteButton
                    id={params.id.toString()}
                    getWarningMessage={(id) => strings.advertiserDeleteConfirm
                        .replace('{{name}}', advertisers?.find((a) => a.id === id)?.name ?? '')}
                    onClick={(id) => remove(id)}
                    aria-label="delete advertiser"
                    hidden={!canDelete}
                />,
            ],
        },
    ];

    return (
        <Box sx={{ ...fillHeight, overflow: 'hidden', p: 2 }}>
            <Stack spacing={1} sx={{ height: '100%', minHeight: 0 }}>
                <Stack direction="row" alignItems="center" justifyContent="space-between">
                    <Typography variant="h6">{strings.advertisers}</Typography>
                    {canCreate &&
                    <Button
                        variant="contained"
                        startIcon={<AddIcon />}
                        size="input"
                        onClick={() => { setEditing(null); setDialogOpen(true); }}
                    >
                        {strings.advertiserNew}
                    </Button>
                    }
                </Stack>

                {/* Said here rather than discovered later: a brand cannot see anything on this screen until it
                    is granted, and a brand's own local advertisers never appear on it. */}
                <Alert severity="info" variant="outlined">{strings.advertiserRosterHelp}</Alert>

                <Box sx={{ flex: 1, minHeight: 300 }}>
                    <DataGrid
                        rows={advertisers ?? []}
                        columns={columns}
                        loading={isPending}
                        getRowId={(row) => row.id}
                        disableRowSelectionOnClick
                        disableColumnMenu
                        localeText={{ noRowsLabel: strings.advertiserNone }}
                    />
                </Box>
            </Stack>

            <AdvertiserDialog
                open={dialogOpen}
                advertiser={editing}
                readOnly={!!editing && !canUpdate}
                onClose={() => setDialogOpen(false)}
            />

            {/* Unmounted while closed so it refetches its grants each time rather than showing a stale list
                from whichever advertiser was opened before. */}
            {granting && (
                <AdvertiserGrantsDialog
                    open
                    advertiser={granting}
                    onClose={() => setGranting(null)}
                />
            )}
        </Box>
    );
};

export default Advertisers;
